import React, { useEffect, useRef, useState, useCallback } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import { useSubscription } from '../../contexts/SubscriptionContext';
import { claimCoupon, getCouponErrorMessage } from '../../api/couponService';
import './WelcomeLandingPage.css';

const DEFAULT_COUPON = 'SPRING14';
const DEFAULT_DAYS = 14;

export default function WelcomeLandingPage() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { currentUser } = useAuth();
  const { fetchSubscriptionStatus } = useSubscription();

  const couponCode = searchParams.get('coupon') || DEFAULT_COUPON;
  const normalizedCouponCode = couponCode.toUpperCase();
  const introDays = normalizedCouponCode === 'TEACHER30' || normalizedCouponCode.startsWith('SCHOOL30')
    ? 30
    : DEFAULT_DAYS;
  const [step, setStep] = useState('intro');
  const [isApplying, setIsApplying] = useState(false);
  const [error, setError] = useState('');
  const [alreadyApplied, setAlreadyApplied] = useState(false);
  const autoAppliedKeyRef = useRef('');

  const applyCoupon = useCallback(async () => {
    if (!currentUser || isApplying) return;
    setIsApplying(true);
    setError('');

    localStorage.removeItem('pending_coupon_code');

    try {
      const result = await claimCoupon(couponCode, currentUser, {
        source: 'welcome_landing',
        url: window.location.href,
      });
      const days = result?.benefit_days || DEFAULT_DAYS;
      await fetchSubscriptionStatus(true);
      setAlreadyApplied(result?.already_claimed === true);
      setStep('success');
      window.localStorage.setItem('welcome_coupon_days', String(days));
    } catch (err) {
      const msg = err.message || '';
      if (msg.includes('already') || msg.includes('이미')) {
        await fetchSubscriptionStatus(true);
        setStep('success');
      } else {
        setError(getCouponErrorMessage(err));
      }
    } finally {
      setIsApplying(false);
    }
  }, [currentUser, couponCode, isApplying, fetchSubscriptionStatus]);

  useEffect(() => {
    if (!currentUser || step !== 'intro') return;
    // 실패하면 isApplying 이 바뀌어 applyCoupon 이 새로 만들어진다. 자동 등록은 계정·쿠폰마다 한 번만.
    // (운영 2026-09-17: 수량이 찬 학교 쿠폰 링크에서 한 학교가 55분간 2,979번 재시도)
    const autoKey = `${currentUser.uid}:${couponCode}`;
    if (autoAppliedKeyRef.current === autoKey) return;
    autoAppliedKeyRef.current = autoKey;
    applyCoupon();
  }, [currentUser, step, couponCode, applyCoupon]);

  const handleStart = () => {
    const redirectUrl = `/welcome?coupon=${couponCode}`;
    navigate(`/auth?tab=signup&redirect=${encodeURIComponent(redirectUrl)}`);
  };

  const handleGoHome = () => {
    navigate('/home');
  };

  const days = localStorage.getItem('welcome_coupon_days') || DEFAULT_DAYS;

  return (
    <div className="welcome-landing__container">
      <div className="welcome-landing__card">

        {/* 헤더 */}
        <div className="welcome-landing__header">
          <p className="welcome-landing__brand">왓퀴즈AI</p>
        </div>

        {/* Step: 인트로 (비로그인) */}
        {step === 'intro' && !currentUser && (
          <div className="welcome-landing__body">
            <div className="welcome-landing__coupon-badge">PRO {introDays}일 무료 체험</div>

            <h1 className="welcome-landing__title">
              교사 업무 시간,<br />3분으로 단축하세요
            </h1>

            <p className="welcome-landing__subtitle">
              생기부, 채점, PPT, 학습지까지<br />
              AI가 자동으로 만들어 드려요.
            </p>

            <div className="welcome-landing__coupon-box">
              <p className="welcome-landing__coupon-label">쿠폰 번호</p>
              <p className="welcome-landing__coupon-code">{couponCode}</p>
              <p className="welcome-landing__coupon-desc">
                회원가입 즉시 PRO {introDays}일 체험이 시작돼요
              </p>
            </div>

            <button
              className="welcome-landing__cta"
              onClick={handleStart}
            >
              무료 체험 시작하기
            </button>

            <p className="welcome-landing__hint">
              Google 계정으로 3초 만에 시작할 수 있어요
            </p>
          </div>
        )}

        {/* Step: 쿠폰 적용 중 (로그인 상태) */}
        {step === 'intro' && currentUser && !error && (
          <div className="welcome-landing__body">
            <div className="welcome-landing__loading">
              <div className="welcome-landing__spinner" />
              <p className="welcome-landing__loading-text">쿠폰을 등록하고 있어요...</p>
            </div>
          </div>
        )}

        {/* Step: 완료 */}
        {step === 'success' && (
          <div className="welcome-landing__body">
            <div className="welcome-landing__success-icon">✓</div>

            <h1 className="welcome-landing__title">
              {alreadyApplied
                ? <>이미 PRO 혜택이<br />적용되어 있어요</>
                : <>PRO {days}일 체험이<br />시작되었어요</>}
            </h1>

            <p className="welcome-landing__subtitle">
              지금 바로 AI 도구를 사용해보세요.
            </p>

            <div className="welcome-landing__features">
              <div className="welcome-landing__feature">
                <span>AI 생기부 자동 생성</span>
              </div>
              <div className="welcome-landing__feature">
                <span>AI 서술형 자동 채점</span>
              </div>
              <div className="welcome-landing__feature">
                <span>AI PPT · 학습지 생성</span>
              </div>
            </div>

            <button
              className="welcome-landing__cta"
              onClick={handleGoHome}
            >
              AI 도구 시작하기
            </button>
          </div>
        )}

        {/* 에러 */}
        {error && (
          <div className="welcome-landing__error">
            <p>{error}</p>
            <button
              className="welcome-landing__retry"
              onClick={applyCoupon}
            >
              다시 시도하기
            </button>
            <button
              className="welcome-landing__retry"
              onClick={handleGoHome}
            >
              쿠폰 없이 둘러보기
            </button>
          </div>
        )}

        {/* 푸터 */}
        <div className="welcome-landing__footer">
          <p>(주)플랙티컬 · 02-949-9158 · hello@whatquiz.co.kr</p>
        </div>
      </div>
    </div>
  );
}
